import { useRef, useState } from 'react';
import { formatSeconds, formatTotal, formatVideoTime } from '../core/time';
import { useApp } from '../state/store';
import { Button, Field, Panel, ScoreBug, Segmented, type TimelineClip } from '../ui';
import { Player, type PlayerHandle } from '../ui/Player';
import { useHotkeys } from '../ui/hotkeys';

const newId = () => `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function MarkScreen() {
  const { project, update, setStep, url, info } = useApp();
  const player = useRef<PlayerHandle>(null);
  const [pending, setPending] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [rate, setRate] = useState<'1' | '2' | '4'>('1');
  const [playing, setPlaying] = useState(false);
  const duration = info?.duration ?? 0;
  const single = project.markMode === 'single';
  const marks = [...project.marks].sort((a, b) => a.start - b.start);
  const total = marks.reduce((s, m) => s + m.end - m.start, 0);

  const add = (start: number, end: number) =>
    update((p) => ({ marks: [...p.marks, { id: newId(), origin: 'mark' as const, start: Math.max(0, start), end: Math.min(duration || end, end) }] }));

  const mark = () => {
    const t = player.current?.time() ?? now;
    if (single) {
      add(t - project.markBefore, t + project.markAfter);
    } else if (pending === null) {
      setPending(t);
    } else {
      const a = Math.min(pending, t);
      add(a, Math.max(Math.max(pending, t), a + 1));
      setPending(null);
    }
  };

  useHotkeys({
    m: mark,
    ' ': () => player.current?.toggle(),
    Escape: () => setPending(null),
    ArrowLeft: () => player.current?.seek(player.current.time() - 1),
    ArrowRight: () => player.current?.seek(player.current.time() + 1),
    'shift+ArrowLeft': () => player.current?.seek(player.current.time() - 10),
    'shift+ArrowRight': () => player.current?.seek(player.current.time() + 10),
  });

  if (!url) return null;
  const tlClips: TimelineClip[] = marks.map((m) => ({ start: m.start, end: m.end, state: 'included' }));
  if (pending !== null) tlClips.push({ start: pending, end: Math.max(pending + 0.5, now), state: 'selected' });

  return (
    <div className="app-grid-side">
      <Player
        ref={player}
        url={url}
        duration={duration}
        rate={Number(rate)}
        clips={tlClips}
        onTime={setNow}
        onPlayingChange={setPlaying}
        topLeft={(t) => <ScoreBug time={formatVideoTime(t)} label={playing ? `Playing ${rate}×` : 'Paused'} />}
        topRight={() => (pending !== null ? <ScoreBug period="Rec" time={formatVideoTime(pending)} label="Press M to end" /> : null)}
        transportEnd={
          <span data-tour="speed">
          <Segmented
            label="Playback speed"
            value={rate}
            onChange={setRate}
            options={[
              { value: '1', label: '1×' },
              { value: '2', label: '2×' },
              { value: '4', label: '4×' },
            ]}
          />
          </span>
        }
      >
        <div data-tour="markbtn" className="app-row" style={{ paddingTop: 4, flexWrap: "wrap" }}>
          <Button variant="primary" size="lg" icon="flag" kbd="M" onClick={mark}>
            {single ? 'Mark highlight' : pending === null ? 'Start highlight' : 'End highlight'}
          </Button>
          {pending !== null && (
            <Button variant="overlay" onClick={() => setPending(null)}>
              Cancel
            </Button>
          )}
          <span className="text-small" style={{ color: 'var(--on-video-muted)' }}>
            {single
              ? `Covers ${project.markBefore} s before and ${project.markAfter} s after the press.`
              : pending === null
                ? 'Press where the highlight starts. Space plays and pauses.'
                : `Recording from ${formatVideoTime(pending)}. Press again where it ends.`}
          </span>
        </div>
      </Player>

      <Panel aria-labelledby="mark-title" className="app-sticky" style={{ gap: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 id="mark-title" className="app-title">
            Mark live
          </h1>
          <p className="text-small muted" style={{ margin: 0 }}>
            Play the match and mark each highlight as it happens. Every clip you mark goes straight into your reel. Marks are video time, so there's no kickoff sync.
          </p>
        </div>
        <div className="hl-field">
          <span className="hl-label">Each press</span>
          <Segmented
            label="Marking mode"
            value={project.markMode}
            onChange={(v) => {
              setPending(null);
              update({ markMode: v });
            }}
            options={[
              { value: 'range', label: 'Start, then end' },
              { value: 'single', label: 'One press' },
            ]}
          />
        </div>
        {single && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <Field label="Before the press" type="number" min={0} max={60} suffix="s" value={project.markBefore} onChange={(e) => update({ markBefore: Math.max(0, Number(e.target.value) || 0) })} />
            <Field label="After the press" type="number" min={0} max={60} suffix="s" value={project.markAfter} onChange={(e) => update({ markAfter: Math.max(0, Number(e.target.value) || 0) })} />
          </div>
        )}
        <div className="app-row-between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <h2 className="app-label">In your reel</h2>
          <span className="time text-small muted">
            {marks.length} {marks.length === 1 ? 'clip' : 'clips'} · {formatTotal(total)}
          </span>
        </div>
        <ul className="mark-list" data-tour="reel">
          {marks.length === 0 && <li className="text-small muted">No highlights yet. Press M or the Mark button while the match plays.</li>}
          {marks.map((m, i) => (
            <li key={m.id} className="mark-item">
              <span className="time text-small muted" style={{ width: 22 }}>
                {i + 1}
              </span>
              <button type="button" onClick={() => player.current?.playRange(m.start, m.end)} style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', background: 'none', border: 0, padding: '4px 0', color: 'inherit', cursor: 'pointer', textAlign: 'left' }} aria-label={`Play clip ${i + 1}`}>
                <span className="time" style={{ fontSize: 14 }}>
                  {formatVideoTime(m.start)} → {formatVideoTime(m.end)}
                </span>
                <span className="time text-small muted">
                  {formatSeconds(m.end - m.start)}
                  {m.origin === 'manual' ? ' · added in review' : ''}
                </span>
              </button>
              <Button variant="ghost" icon="trash" iconOnly aria-label={`Delete clip ${i + 1}`} onClick={() => update((p) => ({ marks: p.marks.filter((x) => x.id !== m.id) }))} />
            </li>
          ))}
        </ul>
        <div className="app-row">
          <Button icon="arrow-left" onClick={() => setStep('start')}>
            Back
          </Button>
          <Button variant="primary" icon="arrow-right" iconAfter disabled={marks.length === 0} onClick={() => setStep('clips')} style={{ flexGrow: 1 }}>
            Review clips
          </Button>
        </div>
      </Panel>
    </div>
  );
}
